import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import apiClient, { tokenStorage } from '@/api/client';
import type {
  AuthWorkspace,
  LoginCredentials,
  RegisterPartnerData,
  User,
  UserRole,
  WorkspaceTokenSession,
} from '@/types';
import {
  applyWorkspaceTokenClaims,
  workspaceTokenMatches,
} from './workspaceSession';

interface WorkspaceListPayload {
  workspaces: AuthWorkspace[];
  active_workspace: AuthWorkspace;
}

interface LoginPayload extends WorkspaceTokenSession {
  user: User;
  session_id: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  viewRole: UserRole | null;
  workspaces: AuthWorkspace[];
  activeWorkspace: AuthWorkspace | null;
  isWorkspaceSwitching: boolean;

  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  registerPartner: (data: RegisterPartnerData) => Promise<void>;
  refreshUser: () => Promise<void>;
  loadWorkspaces: () => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<AuthWorkspace>;
  enable2FA: () => Promise<{ qrCode: string; secret: string }>;
  verify2FA: (code: string) => Promise<void>;
  disable2FA: (code: string) => Promise<void>;
  clearError: () => void;
  setViewRole: (role: UserRole | null) => void;
  clearViewRole: () => void;
}

export const roleDefaultRoutes: Partial<Record<UserRole, string>> = {
  admin: '/v2/dashboard',
  manager: '/v2/dashboard',
  operator: '/v2/production',
  viewer: '/v2/executive',
  user: '/v2/dashboard',
  guest: '/v2/dashboard',
  sales_manager: '/v2/dashboard',
  sales_head: '/v2/executive/communications',
  production: '/v2/production',
  accountant: '/v2/accounting',
  executive: '/v2/executive',
  partner: '/v2/partner-sheet',
  partner_admin: '/v2/partner-sheet',
  partner_user: '/v2/partner-sheet',
  partner_accountant: '/v2/partner-sheet',
};

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      viewRole: null,
      workspaces: [],
      activeWorkspace: null,
      isWorkspaceSwitching: false,

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });
        try {
          const response = await apiClient.post<{ data: LoginPayload }>(
            '/auth/login',
            credentials
          );
          const payload = response.data.data;
          tokenStorage.setTokens(payload.access_token, payload.refresh_token);
          const activeWorkspace = payload.active_workspace ?? null;
          const user = activeWorkspace
            ? applyWorkspaceTokenClaims(
                payload.user,
                payload.access_token,
                activeWorkspace
              )
            : payload.user;
          set({
            user,
            workspaces: payload.workspaces ?? [],
            activeWorkspace,
            isAuthenticated: true,
            isLoading: false,
            isWorkspaceSwitching: false,
            viewRole: null,
          });
        } catch (error) {
          set({ error: errorMessage(error, 'Login failed'), isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await apiClient.post('/auth/logout');
        } catch {
          // The local session must still be cleared when the server is unavailable.
        } finally {
          tokenStorage.clearTokens();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            viewRole: null,
            workspaces: [],
            activeWorkspace: null,
            isWorkspaceSwitching: false,
          });
        }
      },

      registerPartner: async (data: RegisterPartnerData) => {
        set({ isLoading: true, error: null });
        try {
          await apiClient.post('/auth/register', { ...data, role: 'partner' });
          set({ isLoading: false });
        } catch (error) {
          set({
            error: errorMessage(error, 'Registration failed'),
            isLoading: false,
          });
          throw error;
        }
      },

      refreshUser: async () => {
        if (!get().isAuthenticated) return;
        try {
          const response = await apiClient.get<User>('/users/me');
          const token = tokenStorage.getAccessToken();
          const activeWorkspace = get().activeWorkspace;
          set({
            user:
              token && activeWorkspace
                ? applyWorkspaceTokenClaims(
                    response.data,
                    token,
                    activeWorkspace
                  )
                : response.data,
          });
        } catch {
          await get().logout();
        }
      },

      loadWorkspaces: async () => {
        if (!get().isAuthenticated) return;
        const response = await apiClient.get<{ data: WorkspaceListPayload }>(
          '/auth/workspaces'
        );
        set({
          workspaces: response.data.data.workspaces,
          activeWorkspace: response.data.data.active_workspace,
        });
      },

      switchWorkspace: async (workspaceId: string) => {
        const currentUser = get().user;
        if (!currentUser) throw new Error('Authenticated user is required');
        set({ isWorkspaceSwitching: true, error: null });
        try {
          const response = await apiClient.post<{
            data: WorkspaceTokenSession;
          }>(`/auth/workspaces/${workspaceId}:select`);
          const payload = response.data.data;
          if (!workspaceTokenMatches(payload.access_token, payload.active_workspace)) {
            throw new Error('Workspace token does not match selected context');
          }
          const user = applyWorkspaceTokenClaims(
            currentUser,
            payload.access_token,
            payload.active_workspace
          );
          tokenStorage.setTokens(payload.access_token, payload.refresh_token);
          set({
            user,
            workspaces: payload.workspaces,
            activeWorkspace: payload.active_workspace,
            isWorkspaceSwitching: false,
            viewRole: null,
          });
          return payload.active_workspace;
        } catch (error) {
          set({
            error: errorMessage(error, 'Workspace switch failed'),
            isWorkspaceSwitching: false,
          });
          throw error;
        }
      },

      enable2FA: async () => {
        const response = await apiClient.post<{
          qrCode: string;
          secret: string;
        }>('/auth/2fa/enable');
        return response.data;
      },

      verify2FA: async (code: string) => {
        await apiClient.post('/auth/2fa/verify', { code });
        await get().refreshUser();
      },

      disable2FA: async (code: string) => {
        await apiClient.post('/auth/2fa/disable', { code });
        await get().refreshUser();
      },

      clearError: () => set({ error: null }),
      setViewRole: (role) => set({ viewRole: role }),
      clearViewRole: () => set({ viewRole: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        viewRole: state.viewRole,
        workspaces: state.workspaces,
        activeWorkspace: state.activeWorkspace,
      }),
    }
  )
);
